import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const SchemesForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [institutes, setInstitutes] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  
  // Form data for new scheme
  const [formData, setFormData] = useState({
    type: 'training', // Default to training
    code: '',
    name: '',
    SIR: false, // Checkbox for State Institute
    RIR: false, // Checkbox for Regional Institute
    DRC: false, // Checkbox for District Institutes
  });
  
  // Selected institute based on user role (will be set automatically)
  const [selectedInstitute, setSelectedInstitute] = useState(null);
  
  // Get user role from localStorage
  const getUserRole = () => {
    try {
      return localStorage.getItem('userRole') || '';
    } catch (error) {
      console.error('Error accessing localStorage:', error);
      return '';
    }
  };
  
  // Determine the appropriate institute code based on user role
  const getInstituteCode = () => {
    const userRole = getUserRole();
    
    if (userRole === 'state') {
      return 'SIR';
    } else if (userRole === 'region') {
      return 'RIR';
    } else if (userRole === 'district') {
      return 'DRC';
    }
    
    return '';
  };
  
  // Fetch institutes and schemes on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Get institute data using the API service
        const institutesData = await api.master.getInstitutes();
        
        if (institutesData && !institutesData.error) {
          setInstitutes(institutesData.data);
          
          // Set selected institute based on user role
          const instituteCode = getInstituteCode();
          const institute = institutesData.data.find(
            inst => inst.code === instituteCode || 
              (instituteCode === 'SIR' && inst.name === 'SIRD') ||
              (instituteCode === 'RIR' && inst.name === 'RIRD') ||
              (instituteCode === 'DRC' && inst.name === 'DRCP')
          );
          
          if (institute) {
            setSelectedInstitute(institute);
          }
        }
        
        // Fetch initial schemes (training by default)
        await fetchSchemes('training');
      } catch (error) {
        console.error('Error fetching data:', error);
        alert('Failed to load data. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Fetch schemes based on type (training, infra, or admin)
const fetchSchemes = async (type) => {
  setLoading(true);
  try {
    const response = await fetch(`${api.BASE_URL}/master/scheme/${type}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': localStorage.getItem('authToken')
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Failed to load ${type} schemes`);
    }
    
    const data = await response.json();
    setSchemes(data.data || []);
  } catch (error) {
    console.error(`Error fetching ${type} schemes:`, error);
    alert(`Failed to load ${type} schemes. Please check your connection and try again.`);
  } finally {
    setLoading(false);
  }
};
  
  // Handle delete scheme
// 3. Update handleDeleteScheme function (use the same endpoint for all types):
const handleDeleteScheme = async (schemeId) => {
  // Confirm deletion
  if (!window.confirm('Are you sure you want to delete this scheme? This action cannot be undone.')) {
    return;
  }
  
  setDeleteLoading(schemeId);
  try {
    const response = await fetch(`${api.BASE_URL}/master/scheme/${schemeId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': localStorage.getItem('authToken')
      },
      body: JSON.stringify({ status: 'Delete' })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to delete scheme');
    }
    
    const data = await response.json();
    
    if (data && !data.error) {
      alert('Scheme deleted successfully!');
      // Refresh schemes list
      fetchSchemes(formData.type);
    } else {
      throw new Error(data.message || 'Failed to delete scheme');
    }
  } catch (error) {
    console.error('Error deleting scheme:', error);
    alert(`Failed to delete scheme: ${error.message}`);
  } finally {
    setDeleteLoading(null);
  }
};
  
  // Handle input changes for form fields
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: checked ? 1 : 0 // Convert boolean to 1/0 for API
      });
    } else {
      // Special handling for type - fetch schemes when type changes
      if (name === 'type') {
        fetchSchemes(value);
      }
      
      // Special handling for code to uppercase and limit to 3 characters
      if (name === 'code') {
        const uppercaseValue = value.toUpperCase();
        if (uppercaseValue.length <= 3) {
          setFormData({
            ...formData,
            [name]: uppercaseValue
          });
        }
      } else {
        setFormData({
          ...formData,
          [name]: value
        });
      }
    }
  };
  
  // Handle form submission
const handleSubmit = async (e) => {
  e.preventDefault();
  
  // Validation
  if (!formData.code || !formData.name) {
    alert('Please fill in all required fields');
    return;
  }
  
  if (formData.code.length !== 3) {
    alert('Scheme code must be exactly 3 characters');
    return;
  }
  
  if (!formData.SIR && !formData.RIR && !formData.DRC) {
    alert('Please select at least one institute');
    return;
  }
  
  setLoading(true);
  try {
    const response = await fetch(`${api.BASE_URL}/master/scheme`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': localStorage.getItem('authToken')
      },
      body: JSON.stringify(formData)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to add scheme');
    }
    
    const data = await response.json();
    
    if (data && !data.error) {
      alert('Scheme added successfully!');
      // Reset form and refresh schemes list
      setFormData({
        type: formData.type, // Keep the current type
        code: '',
        name: '',
        SIR: false,
        RIR: false,
        DRC: false
      });
      fetchSchemes(formData.type);
      setShowForm(false);
    } else {
      throw new Error(data.message || 'Failed to add scheme');
    }
  } catch (error) {
    console.error('Error adding scheme:', error);
    alert(`Failed to add scheme: ${error.message}`);
  } finally {
    setLoading(false);
  }
};

  
  // Toggle form visibility
  const toggleForm = () => {
    setShowForm(!showForm);
  };
  
  return (
    <div className="bg-gradient-to-b from-blue-50 to-white min-h-screen py-8 px-4">
      <div className="bg-white shadow-lg rounded-xl p-8 max-w-4xl mx-auto border border-gray-100">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 border-b pb-6">
       
          <div className="mt-4 md:mt-0 flex space-x-3">
            <button
              onClick={toggleForm}
              className={`px-4 py-2 rounded-md shadow-sm text-sm font-medium transition-colors ${
                showForm 
                  ? 'bg-gray-200 text-gray-800 border border-gray-300' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {showForm ? 'Cancel' : 'Add New Scheme'}
            </button>
          </div>
        </div>
        
        {/* Loading indicator */}
        {loading && (
          <div className="text-center mb-6 p-4 bg-blue-50 rounded-lg">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-3 text-blue-700 font-medium">Loading data, please wait...</p>
          </div>
        )}
        
        {/* Type of Work Selector (always visible) */}
        <div className="mb-8">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type of Work
          </label>
          <div className="flex space-x-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="type"
                value="training"
                checked={formData.type === 'training'}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-gray-700">Training</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="type"
                value="infra"
                checked={formData.type === 'infra'}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-gray-700">Infrastructure</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="type"
                value="admin"
                checked={formData.type === 'admin'}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-gray-700">Admin</span>
            </label>
          </div>
        </div>
        
        {/* Show institute information */}
        {selectedInstitute && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Your Institute</h3>
            <p className="text-lg font-semibold text-gray-800">{selectedInstitute.name}</p>
            <p className="text-sm text-gray-600">Code: {selectedInstitute.code}</p>
          </div>
        )}
        
        {/* Add New Scheme Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Add New {formData.type === 'training' ? 'Training' : formData.type === 'infra' ? 'Infrastructure' : 'Admin'} Scheme
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Scheme Code */}
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
                  Scheme Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="code"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  placeholder="3-letter code (e.g. ABC)"
                  className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 text-gray-700 focus:border-blue-600 focus:ring focus:ring-blue-200 focus:ring-opacity-50 transition"
                  required
                  maxLength={3}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Enter a unique 3-letter code for the scheme
                </p>
              </div>
              
              {/* Scheme Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Scheme Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter scheme name"
                  className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 text-gray-700 focus:border-blue-600 focus:ring focus:ring-blue-200 focus:ring-opacity-50 transition"
                  required
                />
              </div>
            </div>
            
            {/* Institute Checkboxes */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Institutes <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap gap-6">
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    name="SIR"
                    checked={formData.SIR === 1}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-gray-700">State Institute (SIRD)</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    name="RIR"
                    checked={formData.RIR === 1}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-gray-700">Regional Institute (RIRD)</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    name="DRC"
                    checked={formData.DRC === 1}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-gray-700">District Resource Centers (DRCP)</span>
                </label>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Select which institutes can use this scheme
              </p>
            </div>
            
            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className="px-5 py-2.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:bg-blue-300 disabled:cursor-not-allowed"
              >
                {loading ? 'Adding...' : 'Add Scheme'}
              </button>
            </div>
          </form>
        )}
        
        {/* View Schemes Table - Always visible */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            {formData.type === 'training' 
              ? 'Training' 
              : formData.type === 'infra' 
                ? 'Infrastructure' 
                : 'Admin'} Schemes
          </h3>
          
          {schemes.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Code
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      State (SIRD)
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Regional (RIRD)
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      District (DRCP)
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {schemes.map((scheme) => (
                    <tr key={scheme.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {scheme.code}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {scheme.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {scheme.SIR === 1 ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Yes
                          </span>
                        ) : (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                            No
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {scheme.RIR === 1 ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Yes
                          </span>
                        ) : (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                            No
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {scheme.DRC === 1 ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Yes
                          </span>
                        ) : (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                            No
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <button
                          onClick={() => handleDeleteScheme(scheme.id)}
                          className="text-red-600 hover:text-red-900 focus:outline-none"
                          disabled={deleteLoading === scheme.id}
                        >
                          {deleteLoading === scheme.id ? (
                            <span className="flex items-center">
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Deleting...
                            </span>
                          ) : (
                            "Delete"
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No schemes found for this type. Add a new scheme to get started.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SchemesForm;